import { useTranslation } from "react-i18next";
import { User, Store, Server } from "lucide-react";
import { DetailRow } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { CancellationSource } from "@/types";

interface BookingCancellationSourceProps {
  source: CancellationSource;
  variant?: "inline" | "detail";
  className?: string;
}

const SOURCE_CONFIG: Record<
  CancellationSource,
  { labelKey: string; icon: typeof User; color: string }
> = {
  CUSTOMER: {
    labelKey: "detail.cancellationSource.customer",
    icon: User,
    color: "text-surface-700",
  },
  PARTNER: {
    labelKey: "detail.cancellationSource.partner",
    icon: Store,
    color: "text-surface-700",
  },
  SYSTEM: {
    labelKey: "detail.cancellationSource.system",
    icon: Server,
    color: "text-surface-600",
  },
};

export function BookingCancellationSource({
  source,
  variant = "inline",
  className,
}: BookingCancellationSourceProps) {
  const { t } = useTranslation("bookings");
  const config = SOURCE_CONFIG[source];
  const Icon = config.icon;

  if (variant === "detail") {
    return (
      <DetailRow
        icon={Icon}
        label={t("detail.cancellationSource.label")}
        boxed
        className={cn(
          "rounded-lg border border-surface-200 bg-surface-50 px-3.5 py-2.5",
          className,
        )}
        valueClassName="font-medium"
      >
        {t(config.labelKey)}
      </DetailRow>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-xs text-surface-600",
        className,
      )}
    >
      <Icon className={cn("h-3.5 w-3.5 shrink-0", config.color)} aria-hidden />
      {t(config.labelKey)}
    </span>
  );
}
